<script setup lang="ts">
import { computed } from 'vue'
import { cn } from '@/lib/utils'

// primary/default = ink fill; secondary = grey fill; outline kept for old call sites.
type Variant = 'default' | 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'link'
type Size = 'sm' | 'md' | 'lg' | 'icon'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    size?: Size
    as?: string
    disabled?: boolean
  }>(),
  { variant: 'default', size: 'md', as: 'button' }
)

const classes = computed(() =>
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    {
      default: 'bg-primary text-primary-foreground hover:bg-primary/85',
      primary: 'bg-primary text-primary-foreground hover:bg-primary/85',
      secondary: 'bg-secondary text-secondary-foreground hover:bg-border',
      outline: 'bg-card text-foreground ring-1 ring-inset ring-border hover:bg-muted',
      ghost: 'text-foreground hover:bg-muted',
      destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
      link: 'text-link underline-offset-4 hover:underline !px-0 !h-auto',
    }[props.variant],
    {
      sm: 'h-8 px-3 text-[13px]',
      md: 'h-10 px-4',
      lg: 'h-12 px-5 text-[15px]',
      icon: 'h-10 w-10',
    }[props.size]
  )
)
</script>

<template>
  <component
    :is="as"
    :class="classes"
    :disabled="disabled"
  >
    <slot />
  </component>
</template>
